import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { User, Package, MapPin, Heart, Settings, LogOut, Calendar, Bell, Shield, Eye, Mail, Lock, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

type TabType = 'profile' | 'orders' | 'addresses' | 'favorites' | 'settings';
type AuthMode = 'login' | 'register' | 'forgot';

export function Account() {
  const navigate = useNavigate();
  const [kullanici, setKullanici] = useState<any>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'orders' || tab === 'addresses' || tab === 'favorites' || tab === 'settings') return tab;
    return 'profile';
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({ baslik: '', adres: '', sehir: '', ilce: '', postaKodu: '', telefon: '' });
  const [loading, setLoading] = useState(false);

  // Auth form state
  const [email, setEmail] = useState('');
  const [sifre, setSifre] = useState('');
  const [ad, setAd] = useState('');
  const [soyad, setSoyad] = useState('');
  const [telefon, setTelefon] = useState('');
  const [adres, setAdres] = useState('');
  const [sehir, setSehir] = useState('');
  const [ilce, setIlce] = useState('');
  const [postaKodu, setPostaKodu] = useState('');
  const [dogrulamaKodu, setDogrulamaKodu] = useState('');
  const [kodGonderildi, setKodGonderildi] = useState(false);
  const [resetKod, setResetKod] = useState('');
  const [yeniSifre, setYeniSifre] = useState('');
  const [resetKodGonderildi, setResetKodGonderildi] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('kullanici');
    if (saved) {
      const user = JSON.parse(saved);
      setKullanici(user);
      fetchOrders(user.id);
      fetchAddresses(user.id);
    }
  }, []);

  const fetchOrders = async (userId: number) => {
    try {
      const res = await fetch(`${API_URL}/api/siparislerim/${userId}`);
      const data = await res.json();
      if (Array.isArray(data)) setOrders(data);
    } catch {}
  };

  const fetchAddresses = async (userId: number) => {
    try {
      const res = await fetch(`${API_URL}/api/adreslerim/${userId}`);
      const data = await res.json();
      if (Array.isArray(data)) setAddresses(data);
    } catch {
      setAddresses([]);
    }
  };

  const handleAddAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kullanici?.id) return;
    if (!newAddress.adres || !newAddress.sehir || !newAddress.ilce) {
      toast.error('Lütfen adres, il ve ilçe bilgilerini doldurun');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/adreslerim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kullaniciId: kullanici.id,
          baslik: newAddress.baslik || 'Adres',
          adres: newAddress.adres,
          sehir: newAddress.sehir,
          ilce: newAddress.ilce,
          postaKodu: newAddress.postaKodu,
          telefon: newAddress.telefon || kullanici.telefon,
        }),
      });
      const data = await res.json();
      if (data.basarili) {
        toast.success('Adres eklendi');
        setShowAddressForm(false);
        setNewAddress({ baslik: '', adres: '', sehir: '', ilce: '', postaKodu: '', telefon: '' });
        fetchAddresses(kullanici.id);
      } else {
        toast.error(data.mesaj || 'Adres eklenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
    setLoading(false);
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!kullanici?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/adreslerim/${addressId}?kullaniciId=${kullanici.id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.basarili) {
        toast.success('Adres silindi');
        fetchAddresses(kullanici.id);
      } else {
        toast.error(data.mesaj || 'Adres silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !sifre) { toast.error('Lütfen tüm alanları doldurun'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/giris`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, sifre })
      });
      const data = await res.json();
      if (data.basarili) {
        localStorage.setItem('kullanici', JSON.stringify(data.kullanici));
        setKullanici(data.kullanici);
        fetchOrders(data.kullanici.id);
        fetchAddresses(data.kullanici.id);
        toast.success('Giriş başarılı!');
        navigate('/');
      } else {
        toast.error(data.mesaj || 'Giriş başarısız');
      }
    } catch { toast.error('Bağlantı hatası'); }
    setLoading(false);
  };

  const handleSendCode = async () => {
    if (!email) { toast.error('Lütfen email adresinizi girin'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kayit/dogrulama-kodu-gonder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (data.basarili) { setKodGonderildi(true); toast.success('Doğrulama kodu e-postanıza gönderildi!'); }
      else { toast.error(data.mesaj || 'Kod gönderilemedi'); }
    } catch { toast.error('Bağlantı hatası'); }
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !sifre || !ad || !soyad || !telefon || !dogrulamaKodu) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    if (!adres || !sehir || !ilce) {
      toast.error('Lütfen adres, il ve ilçe bilgilerini doldurun');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kayit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, sifre, ad, soyad, telefon, adres, sehir, ilce, postaKodu, dogrulamaKodu })
      });
      const data = await res.json();
      if (data.basarili) {
        localStorage.setItem('kullanici', JSON.stringify(data.kullanici));
        setKullanici(data.kullanici);
        fetchAddresses(data.kullanici.id);
        toast.success('Kayıt başarılı!');
        navigate('/');
      } else { toast.error(data.mesaj || 'Kayıt başarısız'); }
    } catch { toast.error('Bağlantı hatası'); }
    setLoading(false);
  };

  const handleSendResetCode = async () => {
    if (!email) { toast.error('Lütfen email adresinizi girin'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sifre-sifirlama/kod-gonder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (data.basarili) { setResetKodGonderildi(true); toast.success('Sıfırlama kodu e-postanıza gönderildi!'); }
      else { toast.error(data.mesaj || 'Kod gönderilemedi'); }
    } catch { toast.error('Bağlantı hatası'); }
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !resetKod || !yeniSifre) { toast.error('Lütfen tüm alanları doldurun'); return; }
    if (yeniSifre.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sifre-sifirlama/sifirla`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, kod: resetKod, yeniSifre })
      });
      const data = await res.json();
      if (data.basarili) {
        toast.success('Şifreniz güncellendi! Giriş yapabilirsiniz.');
        setAuthMode('login');
        setSifre('');
        setResetKod('');
        setYeniSifre('');
        setResetKodGonderildi(false);
      } else { toast.error(data.mesaj || 'Şifre sıfırlanamadı'); }
    } catch { toast.error('Bağlantı hatası'); }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('kullanici');
    setKullanici(null);
    setShowLogoutModal(false);
    toast.success('Çıkış yapıldı, güle güle!');
    navigate('/');
  };

  // Giriş yapılmamışsa auth formu göster
  if (!kullanici) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 py-8">
          <div className="max-w-md mx-auto px-4">
            <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Hesabım</h1>
            <div className="bg-white rounded-xl shadow-sm p-6">
              {/* Tab Switch */}
              <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
                <button onClick={() => setAuthMode('login')} className={`flex-1 py-2 rounded-lg font-semibold transition-all ${authMode === 'login' ? 'bg-pink-600 text-white shadow-md' : 'text-gray-700'}`}>Giriş Yap</button>
                <button onClick={() => setAuthMode('register')} className={`flex-1 py-2 rounded-lg font-semibold transition-all ${authMode === 'register' ? 'bg-pink-600 text-white shadow-md' : 'text-gray-700'}`}>Kayıt Ol</button>
              </div>

              {authMode === 'login' ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ornek@email.com" className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Şifre</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                      <input type="password" value={sifre} onChange={e => setSifre(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors disabled:bg-gray-400">
                    {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                  </button>
                  <button type="button" onClick={() => setAuthMode('forgot')} className="w-full text-center text-sm text-pink-600 hover:text-pink-700 font-medium mt-3">
                    Şifremi Unuttum
                  </button>
                </form>
              ) : authMode === 'forgot' ? (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <div className="size-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3"><Lock className="size-8 text-pink-600" /></div>
                    <h3 className="text-lg font-bold text-gray-900">Şifre Sıfırlama</h3>
                    <p className="text-sm text-gray-600 mt-1">E-posta adresinize bir sıfırlama kodu göndereceğiz</p>
                  </div>
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ornek@email.com" className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                      </div>
                    </div>
                    {!resetKodGonderildi ? (
                      <button type="button" onClick={handleSendResetCode} disabled={loading} className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors disabled:bg-gray-400">
                        {loading ? 'Gönderiliyor...' : 'Sıfırlama Kodu Gönder'}
                      </button>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">Sıfırlama Kodu</label>
                          <input type="text" value={resetKod} onChange={e => setResetKod(e.target.value)} placeholder="6 haneli kod" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">Yeni Şifre</label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                            <input type="password" value={yeniSifre} onChange={e => setYeniSifre(e.target.value)} placeholder="En az 6 karakter" className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                          </div>
                        </div>
                        <button type="submit" disabled={loading} className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors disabled:bg-gray-400">
                          {loading ? 'Sıfırlanıyor...' : 'Şifremi Sıfırla'}
                        </button>
                      </>
                    )}
                  </form>
                  <button type="button" onClick={() => { setAuthMode('login'); setResetKodGonderildi(false); }} className="w-full text-center text-sm text-gray-600 hover:text-pink-600 font-medium mt-2">
                    ← Giriş sayfasına dön
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Ad</label>
                      <input type="text" value={ad} onChange={e => setAd(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Soyad</label>
                      <input type="text" value={soyad} onChange={e => setSoyad(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon *</label>
                    <input type="tel" value={telefon} onChange={e => setTelefon(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Adres *</label>
                    <textarea value={adres} onChange={e => setAdres(e.target.value)} rows={2} placeholder="Mahalle, sokak, bina no..." className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">İl *</label>
                      <select value={sehir} onChange={e => setSehir(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required>
                        <option value="">Seçiniz</option>
                        <option value="istanbul">İstanbul</option>
                        <option value="ankara">Ankara</option>
                        <option value="izmir">İzmir</option>
                        <option value="bursa">Bursa</option>
                        <option value="antalya">Antalya</option>
                        <option value="adana">Adana</option>
                        <option value="gaziantep">Gaziantep</option>
                        <option value="konya">Konya</option>
                        <option value="diger">Diğer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">İlçe *</label>
                      <input type="text" value={ilce} onChange={e => setIlce(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Posta Kodu</label>
                    <input type="text" value={postaKodu} onChange={e => setPostaKodu(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Şifre</label>
                    <input type="password" value={sifre} onChange={e => setSifre(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta Doğrulama Kodu</label>
                    <div className="flex gap-2">
                      <input type="text" value={dogrulamaKodu} onChange={e => setDogrulamaKodu(e.target.value)} placeholder="6 haneli kod" className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                      <button type="button" onClick={handleSendCode} disabled={loading || kodGonderildi} className="px-4 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:bg-gray-400 whitespace-nowrap">
                        {kodGonderildi ? 'Gönderildi' : 'Kod Gönder'}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors disabled:bg-gray-400">
                    {loading ? 'Kayıt yapılıyor...' : 'Kayıt Ol'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { id: 'profile' as TabType, name: 'Profilim', icon: User },
    { id: 'orders' as TabType, name: 'Siparişlerim', icon: Package },
    { id: 'addresses' as TabType, name: 'Adreslerim', icon: MapPin },
    { id: 'favorites' as TabType, name: 'Favorilerim', icon: Heart },
    { id: 'settings' as TabType, name: 'Ayarlar', icon: Settings },
  ];

  const getStatusColor = (durum: string) => {
    switch (durum) {
      case 'teslim_edildi': return 'bg-green-100 text-green-700';
      case 'kargoda': case 'dagitimda': return 'bg-blue-100 text-blue-700';
      case 'iptal': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getDurumText = (durum: string) => {
    switch (durum) {
      case 'teslim_edildi': return 'Teslim Edildi';
      case 'kargoda': return 'Kargoda';
      case 'dagitimda': return 'Dağıtımda';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'iptal': return 'İptal';
      default: return 'Beklemede';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Hesabım</h1>
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                  <div className="size-16 bg-pink-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-pink-600">
                      {(kullanici.ad || 'K')[0]}{(kullanici.soyad || '')[0] || ''}
                    </span>
                  </div>
                  <div>
                    <h2 className="font-bold text-gray-900">{kullanici.ad} {kullanici.soyad}</h2>
                    <p className="text-sm text-gray-600">{kullanici.email}</p>
                  </div>
                </div>
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-pink-600 text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`}>
                      <tab.icon className="size-5" />{tab.name}
                    </button>
                  ))}
                </nav>
                <button onClick={() => setShowLogoutModal(true)} className="w-full flex items-center gap-3 px-4 py-3 mt-4 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors">
                  <LogOut className="size-5" />Çıkış Yap
                </button>
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">İstatistikler</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-gray-600">Toplam Sipariş:</span><span className="font-bold text-gray-900">{orders.length}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Toplam Harcama:</span><span className="font-bold text-pink-600">₺{orders.reduce((s, o) => s + Number(o.toplam_tutar || 0), 0).toLocaleString('tr-TR')}</span></div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Kişisel Bilgiler</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Ad</label>
                      <input type="text" defaultValue={kullanici.ad} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Soyad</label>
                      <input type="text" defaultValue={kullanici.soyad} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta</label>
                      <input type="email" defaultValue={kullanici.email} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" readOnly />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon</label>
                      <input type="tel" defaultValue={kullanici.telefon} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                    </div>
                  </div>
                  <button onClick={() => toast.success('Bilgileriniz güncellendi!')} className="mt-6 bg-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors">Bilgileri Kaydet</button>
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Siparişlerim</h2>
                  {orders.length === 0 ? (
                    <div className="text-center py-12"><Package className="size-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">Henüz siparişiniz yok</p></div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <div key={order.id} className="border-2 border-gray-100 rounded-xl p-6 hover:border-pink-300 transition-colors">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-bold text-gray-900">Sipariş #{order.id}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.durum)}`}>{getDurumText(order.durum)}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="size-4" />
                                <span>{new Date(order.created_at).toLocaleDateString('tr-TR')}</span>
                                <span>•</span>
                                <span>{order.odeme_tipi || 'Kredi Kartı'}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-pink-600">₺{Number(order.toplam_tutar).toLocaleString('tr-TR')}</div>
                              <a href={`/siparis-takip?order=${order.id}`} className="text-sm text-pink-600 hover:text-pink-700 font-semibold">Sipariş Detayı</a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Adreslerim</h2>
                    <button
                      type="button"
                      onClick={() => setShowAddressForm(true)}
                      className="inline-flex items-center justify-center gap-2 bg-pink-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
                    >
                      <Plus className="size-5" />
                      Adres Ekle
                    </button>
                  </div>

                  {addresses.length === 0 ? (
                    <div className="text-center py-12">
                      <MapPin className="size-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">Henüz kayıtlı adresiniz yok</p>
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(true)}
                        className="inline-flex items-center gap-2 bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
                      >
                        <Plus className="size-5" />
                        Adres Ekle
                      </button>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {addresses.map((item) => (
                        <div key={item.id} className="border-2 border-gray-100 rounded-xl p-5 hover:border-pink-300 transition-colors relative">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-2">
                              <MapPin className="size-5 text-pink-600 shrink-0" />
                              <h3 className="font-bold text-gray-900">{item.baslik || 'Adres'}</h3>
                              {item.varsayilan && (
                                <span className="text-xs font-semibold bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">Varsayılan</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteAddress(item.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors p-1"
                              aria-label="Adresi sil"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                          <p className="text-gray-700 whitespace-pre-line mb-2">{item.adres}</p>
                          <p className="text-sm text-gray-600">
                            {item.ilce} / {item.sehir}
                            {item.postaKodu ? ` · ${item.postaKodu}` : ''}
                          </p>
                          {item.telefon && <p className="text-sm text-gray-600 mt-1">{item.telefon}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddressForm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-gray-900">Yeni Adres</h3>
                          <button type="button" onClick={() => setShowAddressForm(false)} className="text-gray-400 hover:text-gray-700">
                            <X className="size-5" />
                          </button>
                        </div>
                        <form onSubmit={handleAddAddress} className="space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Adres Başlığı</label>
                            <input
                              type="text"
                              value={newAddress.baslik}
                              onChange={(e) => setNewAddress((p) => ({ ...p, baslik: e.target.value }))}
                              placeholder="Ev, İş vb."
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Adres</label>
                            <textarea
                              value={newAddress.adres}
                              onChange={(e) => setNewAddress((p) => ({ ...p, adres: e.target.value }))}
                              rows={3}
                              required
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-2">İl</label>
                              <input
                                type="text"
                                value={newAddress.sehir}
                                onChange={(e) => setNewAddress((p) => ({ ...p, sehir: e.target.value }))}
                                required
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-2">İlçe</label>
                              <input
                                type="text"
                                value={newAddress.ilce}
                                onChange={(e) => setNewAddress((p) => ({ ...p, ilce: e.target.value }))}
                                required
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-2">Posta Kodu</label>
                              <input
                                type="text"
                                value={newAddress.postaKodu}
                                onChange={(e) => setNewAddress((p) => ({ ...p, postaKodu: e.target.value }))}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon</label>
                              <input
                                type="tel"
                                value={newAddress.telefon}
                                onChange={(e) => setNewAddress((p) => ({ ...p, telefon: e.target.value }))}
                                placeholder={kullanici?.telefon || ''}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500"
                              />
                            </div>
                          </div>
                          <div className="flex gap-3 pt-2">
                            <button
                              type="button"
                              onClick={() => setShowAddressForm(false)}
                              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                            >
                              İptal
                            </button>
                            <button
                              type="submit"
                              disabled={loading}
                              className="flex-1 bg-pink-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-pink-700 disabled:bg-gray-400"
                            >
                              {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Favorites Tab */}
              {activeTab === 'favorites' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Favorilerim</h2>
                  <div className="text-center py-12">
                    <Heart className="size-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Favori ürünlerinizi görüntülemek için Favoriler sayfasına gidin</p>
                    <a href="/favoriler" className="inline-block bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors">Favorilere Git</a>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-6"><Bell className="size-6 text-pink-600" /><h2 className="text-2xl font-bold text-gray-900">Bildirim Ayarları</h2></div>
                    <div className="space-y-4">
                      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
                        <div><div className="font-semibold text-gray-900">E-posta Bildirimleri</div><div className="text-sm text-gray-600">Sipariş ve kampanya bildirimleri al</div></div>
                        <input type="checkbox" defaultChecked className="size-5" />
                      </label>
                      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
                        <div><div className="font-semibold text-gray-900">SMS Bildirimleri</div><div className="text-sm text-gray-600">Kargo ve özel fırsatlar için SMS al</div></div>
                        <input type="checkbox" defaultChecked className="size-5" />
                      </label>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-6"><Shield className="size-6 text-pink-600" /><h2 className="text-2xl font-bold text-gray-900">Gizlilik & Güvenlik</h2></div>
                    <div className="space-y-3">
                      <a href="#" className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"><span className="font-medium text-gray-900">KVKK Aydınlatma Metni</span><Eye className="size-5 text-gray-400" /></a>
                      <a href="#" className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"><span className="font-medium text-gray-900">Gizlilik Politikası</span><Eye className="size-5 text-gray-400" /></a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="size-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><LogOut className="size-8 text-red-600" /></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Çıkış Yap</h3>
              <p className="text-gray-600">Hesabınızdan çıkış yapmak istediğinizden emin misiniz?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutModal(false)} className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors">İptal</button>
              <button onClick={handleLogout} className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors">Çıkış Yap</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
